import { useEffect, useState } from 'react';

/**
 * What Bailey is doing right now, wherever he was asked.
 *
 * The pet and the Bailey page both publish here, so a reply running in one
 * tab (or on the Bailey page) shows on the pet everywhere else. Kept in
 * localStorage per person: other tabs hear it through the storage event, this
 * tab through a custom event.
 *
 * Shape: { phase: 'working'|'done'|'error', title, activity, tool, preview,
 *          sessionId, at }
 *
 * A 'working' state older than STALE_MS is treated as gone: a tab closed
 * mid-reply never publishes its end, and a pet stuck on "Thinking" forever is
 * worse than a pet that forgets.
 */

const EVENT = 'beu:bailey-run-changed';
const DISMISS_EVENT = 'beu:bailey-run-dismissed';
const STALE_MS = 3 * 60 * 1000;
// A failed reply is news for a moment, not something to bring back on every
// page for the rest of the day
const ERROR_MS = 2 * 60 * 1000;
const key = () => `beu:bailey-run:${localStorage.getItem('userId') || 'anon'}`;
const dismissKey = () => `beu:bailey-run-dismissed:${localStorage.getItem('userId') || 'anon'}`;

function fresh(run) {
  if (!run) return null;
  if (run.phase === 'working' && Date.now() - (run.at || 0) > STALE_MS) return null;
  if (run.phase === 'error' && Date.now() - (run.at || 0) > ERROR_MS) return null;
  return run;
}

// When the salon last closed the bubble, so a closed bubble stays closed on
// the next page (and in other tabs)
export function getDismissedAt() {
  try { return Number(localStorage.getItem(dismissKey())) || 0; } catch (e) { return 0; }
}
export function setDismissedAt(at) {
  try { localStorage.setItem(dismissKey(), String(at || 0)); } catch (e) { /* this page still remembers */ }
  try { window.dispatchEvent(new CustomEvent(DISMISS_EVENT, { detail: at || 0 })); } catch (e) { /* ignore */ }
  return at || 0;
}
// The same, kept up to date: closed here, in another tab, or on another page
export function useDismissedAt() {
  const [at, setAt] = useState(getDismissedAt);
  useEffect(() => {
    const local = (e) => setAt(Number(e.detail) || 0);
    const other = (e) => { if (e.key === dismissKey()) setAt(getDismissedAt()); };
    window.addEventListener(DISMISS_EVENT, local);
    window.addEventListener('storage', other);
    return () => { window.removeEventListener(DISMISS_EVENT, local); window.removeEventListener('storage', other); };
  }, []);
  return at;
}

export function getRun() {
  try {
    const raw = localStorage.getItem(key());
    return fresh(raw ? JSON.parse(raw) : null);
  } catch (e) {
    return null;
  }
}

export function publishRun(patch) {
  const next = patch === null ? null : { ...(getRun() || {}), ...patch, at: Date.now() };
  try {
    if (next) localStorage.setItem(key(), JSON.stringify(next));
    else localStorage.removeItem(key());
  } catch (e) { /* storage unavailable: this tab still hears the event */ }
  try { window.dispatchEvent(new CustomEvent(EVENT, { detail: next })); } catch (e) { /* ignore */ }
  return next;
}

export function clearRun() { return publishRun(null); }

export function useBaileyRun() {
  const [run, setRun] = useState(getRun);
  useEffect(() => {
    const local = (e) => setRun(fresh(e.detail));
    const other = (e) => { if (e.key === key()) setRun(getRun()); };
    window.addEventListener(EVENT, local);
    window.addEventListener('storage', other);
    return () => { window.removeEventListener(EVENT, local); window.removeEventListener('storage', other); };
  }, []);
  return run;
}

/** First sentence-ish of a reply, for the bubble. Markdown stripped. */
export function previewOf(text, max = 110) {
  const plain = String(text || '')
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/[#*_`>|]/g, '')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
  if (!plain) return '';
  const m = plain.match(/^(.{20,}?[.!?])(\s|$)/);
  const first = m ? m[1] : plain;
  return first.length > max ? `${first.slice(0, max - 1).trimEnd()}…` : first;
}
